// src/pages/LoginPage.tsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AuthLayout from '../components/auth/AuthLayout';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import { buttonClassName } from '../components/ui/buttonStyles';
import { FormField, Input } from '../components/ui/Field';
import { isDarkNow } from '../ui/darkMode';

const useAppVersion = (): string | null => {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ version?: string }>('/version')
      .then((r) => setVersion(r.data?.version ?? null))
      .catch(() => setVersion(null));
  }, []);
  return version;
};

type LoginErrorType = 'EMAIL_NOT_VERIFIED' | 'ACCOUNT_PENDING' | 'ACCOUNT_BLOCKED' | 'INVALID_PROFILE_CONFIGURATION' | 'INVALID_PASSWORD' | 'USER_NOT_FOUND' | 'INVALID_CREDENTIALS' | 'HCAPTCHA_REQUIRED' | 'HCAPTCHA_INVALID' | 'HCAPTCHA_UNAVAILABLE' | 'SERVER_ERROR' | 'GENERIC';

const ERROR_MESSAGES: Record<LoginErrorType, string> = {
  EMAIL_NOT_VERIFIED: 'Seu email ainda não foi verificado. Verifique sua caixa de entrada (e a pasta de spam) para encontrar o link de confirmação.',
  ACCOUNT_PENDING: 'Sua conta está aguardando aprovação do administrador. Você receberá um email quando for aprovado.',
  ACCOUNT_BLOCKED: 'Sua conta foi bloqueada. Entre em contato com o administrador do sistema.',
  INVALID_PROFILE_CONFIGURATION: 'O perfil Secretaria precisa estar vinculado a uma secretaria. Entre em contato com o administrador.',
  INVALID_PASSWORD: 'Senha incorreta. Verifique e tente novamente.',
  USER_NOT_FOUND: 'CPF/CNPJ não encontrado. Verifique os dados ou cadastre-se.',
  INVALID_CREDENTIALS: 'CPF/CNPJ ou senha inválidos. Verifique e tente novamente.',
  HCAPTCHA_REQUIRED: 'Confirme que você é uma pessoa antes de acessar o sistema.',
  HCAPTCHA_INVALID: 'A verificação de segurança expirou ou não foi aceita. Tente novamente.',
  HCAPTCHA_UNAVAILABLE: 'A verificação de segurança está indisponível. Tente novamente em instantes.',
  SERVER_ERROR: 'Erro interno no servidor. Tente novamente mais tarde.',
  GENERIC: 'Erro ao conectar ao servidor. Tente novamente mais tarde.',
};

const RESEND_COOLDOWN_SECONDS = 60;

type HCaptchaConfig = {
  enabled: boolean;
  siteKey?: string;
};

const LoginPage = () => {
  const appVersion = useAppVersion();
  const [document, setDocument] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorType, setErrorType] = useState<LoginErrorType | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [hCaptchaConfig, setHCaptchaConfig] = useState<HCaptchaConfig | null>(null);
  const [hCaptchaRequired, setHCaptchaRequired] = useState(false);
  const [hCaptchaToken, setHCaptchaToken] = useState('');
  const [hCaptchaLoadError, setHCaptchaLoadError] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as any)?.message as string | undefined;

  const isExpired = new URLSearchParams(location.search).get('expired') === 'true';
  const expiredMessage = isExpired ? 'Sua sessão expirou. Por favor, faça login novamente.' : null;

  useEffect(() => {
    return () => { if (cooldownTimer.current) clearInterval(cooldownTimer.current); };
  }, []);

  useEffect(() => {
    let active = true;
    api.get<HCaptchaConfig>('/auth/hcaptcha-config')
      .then(({ data }) => {
        if (!active) return;
        if (data.enabled && !data.siteKey) throw new Error('hCaptcha sitekey ausente');
        setHCaptchaConfig(data);
      })
      .catch(() => {
        if (active) setHCaptchaLoadError(true);
      });
    return () => { active = false; };
  }, []);

  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownTimer.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendSuccess(false);
    const cleanDocument = document.replace(/\D/g, '');
    try {
      await api.post('/users/resend-verification', { document: cleanDocument });
      setResendSuccess(true);
      startCooldown();
    } catch {
      // ainda mostra cooldown para evitar enumeração
      startCooldown();
    } finally {
      setResendLoading(false);
    }
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setHCaptchaRequired(false);
    setHCaptchaToken('');
    captchaRef.current?.resetCaptcha();
    if (value.length <= 11) {
      setDocument(value
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2'));
    } else {
      setDocument(value
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2'));
    }
  };

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [successMessage, navigate, location.pathname]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorType(null);
    setResendSuccess(false);
    if (hCaptchaRequired && !hCaptchaToken) {
      setErrorType('HCAPTCHA_REQUIRED');
      return;
    }
    setLoading(true);
    const cleanDocument = document.replace(/\D/g, '');
    try {
      const success = await login(cleanDocument, password, hCaptchaToken);
      if (success) {
        navigate('/dashboard');
      } else {
        setErrorType('INVALID_CREDENTIALS');
      }
    } catch (err: any) {
      const code = err?.response?.data?.error as LoginErrorType | undefined;
      if (err?.response?.data?.captchaRequired || code === 'HCAPTCHA_REQUIRED') {
        setHCaptchaRequired(true);
      }
      if (code && code in ERROR_MESSAGES) {
        setErrorType(code);
      } else {
        setErrorType('GENERIC');
      }
    } finally {
      setLoading(false);
      setHCaptchaToken('');
      captchaRef.current?.resetCaptcha();
    }
  };

  return (
    <AuthLayout footerExtra={appVersion ? <div>Versão {appVersion}</div> : null}>
      <div className="space-y-4">
        {expiredMessage && <Alert variant="warning">{expiredMessage}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {errorType && errorType !== 'EMAIL_NOT_VERIFIED' && (
          <Alert variant="danger">{ERROR_MESSAGES[errorType]}</Alert>
        )}
        {errorType === 'EMAIL_NOT_VERIFIED' && (
          <Alert
            variant="warning"
            action={(
              <div className="space-y-1">
                {resendSuccess && (
                  <p className="text-xs text-success-fg">Novo link enviado! Verifique seu email.</p>
                )}
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0 || resendLoading || !document}
                  className="text-xs font-medium underline underline-offset-2 transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {resendLoading
                    ? 'Enviando...'
                    : resendCooldown > 0
                    ? `Reenviar email (aguarde ${resendCooldown}s)`
                    : 'Reenviar email de verificação'}
                </button>
              </div>
            )}
          >
            {ERROR_MESSAGES.EMAIL_NOT_VERIFIED}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField htmlFor="document" label="CPF ou CNPJ">
            <Input
              type="text"
              id="document"
              value={document}
              onChange={handleDocumentChange}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              maxLength={18}
              required
              autoComplete="username"
              startIcon={(
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              )}
            />
          </FormField>

          <FormField
            htmlFor="password"
            label="Senha"
            labelAside={(
              <Link to="/forgot-password" tabIndex={-1} className="text-xs text-link transition hover:text-link-hover">
                Esqueci minha senha
              </Link>
            )}
          >
            <Input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite sua senha"
              required
              autoComplete="current-password"
              startIcon={(
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              )}
              endAdornment={(
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  className="flex h-8 w-8 items-center justify-center rounded text-fg-subtle transition hover:text-fg"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              )}
            />
          </FormField>

          {/* hCaptcha */}
          {hCaptchaConfig?.enabled && hCaptchaRequired && hCaptchaConfig.siteKey && (
            <div className="flex min-h-[78px] items-center justify-center">
              <HCaptcha
                ref={captchaRef}
                sitekey={hCaptchaConfig.siteKey}
                theme={isDarkNow() ? 'dark' : 'light'}
                languageOverride="pt-BR"
                onVerify={setHCaptchaToken}
                onExpire={() => setHCaptchaToken('')}
                onError={() => {
                  setHCaptchaToken('');
                  setErrorType('HCAPTCHA_UNAVAILABLE');
                }}
              />
            </div>
          )}
          {!hCaptchaConfig && (
            <div className="flex min-h-[78px] items-center justify-center">
              {hCaptchaLoadError ? (
                <p className="text-center text-sm text-danger-text">
                  Não foi possível carregar a configuração de segurança.
                </p>
              ) : (
                <p className="text-sm text-fg-muted">Carregando configuração de segurança...</p>
              )}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={hCaptchaLoadError || !hCaptchaConfig}
          >
            {loading ? 'Entrando...' : (
              <>
                Acessar o Sistema
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </Button>
        </form>

        <div className="text-center text-sm text-fg-muted">
          Não tem uma conta?{' '}
          <Link to="/register" className="font-medium text-link transition hover:text-link-hover">
            Cadastrar-se
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a
            href={`${window.location.pathname.startsWith('/emendas') ? '/emendas' : ''}/guia/guia-entidade.html`}
            className={buttonClassName({ variant: 'secondary' })}
          >
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2z"/><path d="M22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7z"/>
            </svg>
            Guia da entidade
          </a>
          <Link to="/contato" className={buttonClassName({ variant: 'secondary' })}>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
            Fale conosco
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
